import React, { useEffect, useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { Clock, ChevronDown, ChevronUp, CheckCircle, Loader, AlertCircle, X, Download } from 'lucide-react';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'jazzcash', label: 'JazzCash' },
  { value: 'easypaisa', label: 'EasyPaisa' },
  { value: 'bank', label: 'Bank Transfer' },
];

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'all', label: 'All Time' },
];

const PendingActivationPayments = () => {
  const [groups, setGroups] = useState([]);
  const [totalPendingCount, setTotalPendingCount] = useState(0);
  const [totalPendingAmount, setTotalPendingAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [officers, setOfficers] = useState([]);

  // Filters
  const [preset, setPreset] = useState('month');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [officerFilter, setOfficerFilter] = useState('');

  // Clear payment modal
  const [clearing, setClearing] = useState(null); // { id, username, amount }
  const [clearMethod, setClearMethod] = useState('');
  const [clearSubmitting, setClearSubmitting] = useState(false);
  const [clearError, setClearError] = useState('');

  const buildParams = useCallback(() => {
    const today = new Date().toLocaleDateString('en-CA');
    const params = {};
    if (dateFrom || dateTo) {
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
    } else if (preset === 'today') {
      params.date_from = today;
      params.date_to = today;
    } else if (preset === 'week') {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      params.date_from = d.toLocaleDateString('en-CA');
    } else if (preset === 'month') {
      const d = new Date();
      d.setDate(1);
      params.date_from = d.toLocaleDateString('en-CA');
    }
    // 'all': no date params
    return params;
  }, [preset, dateFrom, dateTo]);

  const fetchPending = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/activations/pending-payments/', { params: buildParams() });
      const data = res.data;
      setGroups(data.groups || []);
      setOfficers(data.officers || []);
      setTotalPendingCount(data.total_pending_count || 0);
      setTotalPendingAmount(data.total_pending_amount || 0);
      // Auto-expand all groups
      const expanded = {};
      (data.groups || []).forEach(g => {
        expanded[`${g.date}-${g.officer_id ?? 'unassigned'}`] = true;
      });
      setExpandedGroups(expanded);
    } catch (err) {
      console.error('Failed to fetch pending payments:', err);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const openClearModal = (entry) => {
    setClearing(entry);
    setClearMethod('');
    setClearError('');
  };

  const closeClearModal = () => {
    setClearing(null);
    setClearMethod('');
    setClearError('');
  };

  const handleClear = async () => {
    if (!clearMethod) { setClearError('Select a payment method.'); return; }
    try {
      setClearSubmitting(true);
      setClearError('');
      await api.patch(`/activations/${clearing.id}/clear-payment/`, { clear_method: clearMethod });
      closeClearModal();
      await fetchPending();
    } catch (err) {
      setClearError(err.response?.data?.error || 'Failed to clear payment. Try again.');
    } finally {
      setClearSubmitting(false);
    }
  };

  // Client-side filtered groups by recovery officer
  const visibleGroups = officerFilter
    ? groups.filter(g => String(g.officer_id) === officerFilter)
    : groups;

  const visiblePendingCount = visibleGroups.reduce((s, g) => s + g.pending_count, 0);
  const visiblePendingAmount = visibleGroups.reduce((s, g) => s + g.pending_amount, 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-PK', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    });
  };

  const handleExport = () => {
    const rows = [];
    visibleGroups.forEach(group => {
      group.entries.forEach(entry => {
        rows.push({
          'Date': group.date,
          'Officer': group.officer_name,
          'Entry #': entry.entry_no,
          'Username': entry.username,
          'Customer Name': entry.customer_name || '',
          'Package': entry.package_name,
          'Amount (PKR)': entry.amount,
          'Payment Method': entry.payment_method,
          'Type': entry.activation_type,
          'Entered By': entry.entered_by_name || '',
          'Details': entry.details,
        });
      });
    });

    if (rows.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pending Payments');

    const officerLabel = officerFilter
      ? officers.find(o => String(o.id) === officerFilter)?.name?.replace(/\s+/g, '-') || 'Officer'
      : 'All-Officers';
    const today = new Date().toLocaleDateString('en-CA');
    XLSX.writeFile(wb, `Pending-Payments-${officerLabel}-${today}.xlsx`);
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Activation Pending Payments</h1>
          <p className="text-sm text-gray-500">Review and clear outstanding activation payments by date</p>
        </div>
        <button
          type="button"
          onClick={handleExport}
          disabled={visibleGroups.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
        >
          <Download size={15} />
          Export Excel
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className={`p-4 rounded-xl shadow-sm border ${visiblePendingCount > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-100'}`}>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <Clock size={12} className="text-amber-500" /> Pending Entries
          </p>
          <p className={`text-2xl font-bold ${visiblePendingCount > 0 ? 'text-amber-700' : 'text-gray-900'}`}>
            {visiblePendingCount}
          </p>
          {officerFilter && totalPendingCount !== visiblePendingCount && (
            <p className="text-xs text-gray-400 mt-0.5">{totalPendingCount} total this period</p>
          )}
        </div>
        <div className={`p-4 rounded-xl shadow-sm border ${visiblePendingAmount > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'}`}>
          <p className="text-xs text-gray-500">Pending Amount</p>
          <p className={`text-2xl font-bold ${visiblePendingAmount > 0 ? 'text-red-700' : 'text-gray-900'}`}>
            PKR {visiblePendingAmount.toLocaleString()}
          </p>
          {officerFilter && totalPendingAmount !== visiblePendingAmount && (
            <p className="text-xs text-gray-400 mt-0.5">PKR {totalPendingAmount.toLocaleString()} total</p>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {DATE_PRESETS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => { setPreset(p.value); setDateFrom(''); setDateTo(''); }}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    preset === p.value && !dateFrom && !dateTo
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5 sm:ml-auto">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPreset(''); }}
                className="px-2 py-1 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-400">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPreset(''); }}
                className="px-2 py-1 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Recovery officer filter */}
          {officers.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500 font-medium shrink-0">Officer:</span>
              <button
                onClick={() => setOfficerFilter('')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  officerFilter === '' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All Officers
              </button>
              {officers.map((o) => (
                <button
                  key={o.id}
                  onClick={() => setOfficerFilter(String(o.id))}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    officerFilter === String(o.id) ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {o.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Groups */}
      {loading ? (
        <div className="text-center py-16">
          <Loader className="animate-spin text-blue-600 mx-auto" size={28} />
        </div>
      ) : visibleGroups.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <p className="font-semibold text-gray-700">No pending payments</p>
          <p className="text-sm text-gray-400 mt-1">
            {officerFilter ? 'No pending payments for this officer in the selected period.' : 'All activation payments are cleared for this period.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {visibleGroups.map((group) => {
            const groupKey = `${group.date}-${group.officer_id ?? 'unassigned'}`;
            return (
            <div key={groupKey} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Group header */}
              <button
                type="button"
                onClick={() => toggleGroup(groupKey)}
                className="w-full flex items-center justify-between px-4 py-4 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="shrink-0">
                    <span className="text-xs font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                      {group.pending_count} pending
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 text-sm">{formatDate(group.date)}</p>
                    <p className="text-xs text-gray-500 truncate">
                      Officer: <span className="font-medium text-gray-700">{group.officer_name}</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-sm font-bold text-red-600">PKR {group.pending_amount.toLocaleString()}</span>
                  {expandedGroups[groupKey] ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </div>
              </button>

              {/* Entries */}
              {expandedGroups[groupKey] && (
                <div className="border-t border-gray-100">
                  {/* Desktop table */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">#</th>
                          <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Username</th>
                          <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Package</th>
                          <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                          <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Method</th>
                          <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Entered By</th>
                          <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Details</th>
                          <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {group.entries.map((entry) => (
                          <tr key={entry.id} className="hover:bg-amber-50/30 transition-colors">
                            <td className="px-4 py-3 text-sm font-bold text-gray-500">{entry.entry_no}</td>
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium text-gray-900">{entry.username}</p>
                              {entry.customer_name && <p className="text-xs text-gray-400">{entry.customer_name}</p>}
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-600 font-medium">{entry.package_name}</td>
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                              PKR {entry.amount.toLocaleString()}
                            </td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                                {entry.payment_method}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-400">
                              {entry.entered_by_name || '—'}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-400 max-w-[160px] truncate">
                              {entry.details || '—'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                onClick={() => openClearModal(entry)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
                              >
                                <CheckCircle size={13} />
                                Clear
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="sm:hidden divide-y divide-gray-100">
                    {group.entries.map((entry) => (
                      <div key={entry.id} className="p-4 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs font-bold text-gray-400">#{entry.entry_no}</span>
                            <span className="font-medium text-gray-900 text-sm">{entry.username}</span>
                          </div>
                          <p className="text-xs text-gray-500">{entry.package_name} · PKR {entry.amount.toLocaleString()}</p>
                          {entry.details && <p className="text-xs text-gray-400 mt-0.5">{entry.details}</p>}
                        </div>
                        <button
                          type="button"
                          onClick={() => openClearModal(entry)}
                          className="shrink-0 flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <CheckCircle size={13} />
                          Clear
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
          })}
        </div>
      )}

      {/* Clear Payment Modal */}
      {clearing && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-bold text-gray-900">Clear Payment</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {clearing.username} · PKR {clearing.amount.toLocaleString()}
                </p>
              </div>
              <button type="button" onClick={closeClearModal} className="text-gray-400 hover:text-gray-600 p-1">
                <X size={18} />
              </button>
            </div>

            <label className="block text-xs font-medium text-gray-600 mb-2">Payment Method Received</label>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {PAYMENT_METHODS.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setClearMethod(m.value)}
                  className={`py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                    clearMethod === m.value
                      ? 'bg-green-100 border-green-400 text-green-800'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {clearError && (
              <div className="mb-3 flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <AlertCircle size={14} />
                {clearError}
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={closeClearModal}
                className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleClear}
                disabled={clearSubmitting || !clearMethod}
                className="flex-1 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {clearSubmitting ? <Loader size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                Confirm Clear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingActivationPayments;
